/**
 * buildResearchDeliverableMessage.ts — BUG-DR-ATTACH-WHEN-NO-STREAMING
 * (2026-05-21, Slack C0AM8U804KZ/1778281880.499349 msg 1779385039.677619,
 * screenshot F0B5AFF7W5T.png).
 *
 * Pure builder extracted from `useGlobalTools.ts`'s prest0n-session SSE
 * `onDone` callback so the deliverable-message shape (PDF + DOCX
 * attachment tile, sourceTag `research_result`) is testable in isolation
 * (no React, no hook, no jest-dom).
 *
 * Context — the demo-blocker this neutralizes:
 *   On the Skip-to-Deep-Research full-pipeline turn, the backend
 *   completes the entire DR pipeline end-to-end:
 *     clarifying_card_resumed
 *       → deep_research.stage_time_ms (Qualification + Blueprint + Final Synthesis)
 *       → deliverable_persist.uploaded x2 (PDF + DOCX uploaded to GCS)
 *       → Task completed
 *   The SSE `done` event carries `attachmentsSerialized=[PDF, DOCX]` and
 *   `surfaceOwnedByTool=true`. Frontend behavior pre-fix:
 *     1. PR #198 zeroed the planner's `finalResponse` for surface-owning
 *        tools → backend never emits an SSE `text` chunk for this turn.
 *     2. Frontend's `startStreamingMessage` is only ever called from
 *        within `onTextChunk` → `streamingMessageId` stays null for the
 *        whole turn.
 *     3. `onDone` hits the `streamingMessageId === null` branch where the
 *        only path was `decideOnDoneFallback` (PR #207 Layer D), which
 *        correctly suppresses the bare-`Done.` system bubble but had no
 *        path to surface attachments → PDF + DOCX silently dropped.
 *     4. User saw: stage labels → `Done.` bubble → infinite spinner. No
 *        deliverable tile.
 *
 * Fix shape:
 *   Add a NEW orphan-attachment branch in `onDone` that fires BEFORE the
 *   existing streamingMessageId-branch check: when
 *   `streamingMessageId === null` AND `attachmentsSerialized.length > 0`,
 *   build the same ChatMessage shape the streaming-finalize branch would
 *   have produced and emit it via `helpers.addMessage(...)`. This helper
 *   owns the shape construction so the `onDone` callsite is a thin
 *   detect + addMessage + removeThinkingIndicator.
 *
 * What the helper does (mirrors useGlobalTools.ts:2415-2440 verbatim
 * for the attachment-mapping shape — same filter, same map, same
 * dataUrl fallback):
 *   - Filter null/undefined attachments and entries lacking `.name`
 *   - Map each into the renderer-shaped attachment ({name, type, size,
 *     storageUrl, dataUrl}). dataUrl is only synthesized from inline
 *     base64 `data` if storageUrl is absent — Storage is the preferred
 *     download source.
 *   - Returns null if no valid attachments remain (caller falls through
 *     to existing fallback decision).
 *   - Returns ChatMessage with sourceTag='research_result' so
 *     getAttachmentVariant() in ChatAttachment.tsx:280 routes through
 *     the deep-research attachment variant (download buttons, no
 *     Storage hop).
 *
 * Why a separate file:
 *   - Testable without spinning up jest-dom or React hook harness.
 *   - Pinning the shape in unit tests catches regressions if the
 *     renderer ever changes its dataUrl / storageUrl contract.
 *   - Mirror of the streaming-finalize branch's attachment shape — if
 *     the two paths ever drift, both will fail the same test.
 *
 * Token usage (2026-07-13): the ⓘ info popup must show accurate token usage
 * on EVERY message/card. This builder now accepts optional pre-mapped
 * `tokenUsage` / `tokenUsages` (built by buildMessageTokenUsage from the SSE
 * `done` args) and stamps them onto the deliverable message, so a
 * full-pipeline Deep Research report no longer shows "no extensive compute
 * was necessary". Both onDone branches build usage through the same shared
 * mapper, so the streaming and orphan-deliverable paths cannot drift.
 */

import { toSerializedAttachment, type RawAttachmentRecord } from '../../../components/GlobalChatSystem/attachmentRecords';
import { deriveTaskAnchorId } from '../../../types/taskAnchor';
import type {
  ChatMessage,
  SerializedAttachment,
  TokenUsageMetadata,
} from '../../../components/GlobalChatSystem/types';

/**
 * Wire shape of an attachment as it arrives on the SSE `done` event.
 * Mirrors the loose argument type in useGlobalTools.ts:2390 — the SSE
 * deserializer is forgiving so we accept partial / typed-loosely inputs
 * and filter defensively below.
 */
export interface RawDeliverableAttachment {
  name: string;
  type: string;
  size: number;
  /**
   * Firebase Storage download URL when the deliverable was uploaded
   * (the normal full-pipeline DR path — see deliverable_persist.uploaded
   * in functions/src/deepResearch/deliverables.ts). Preferred over
   * inline base64.
   */
  storageUrl?: string;
  /**
   * Inline base64 payload fallback for legacy or non-Storage paths.
   * Converted to a `data:<type>;base64,<...>` URL below so
   * AttachmentsSection treats it as a first-class download source.
   */
  data?: string;
}

/**
 * Build a ChatMessage representing a one-shot deep-research deliverable
 * bundle (PDF + DOCX, etc.) emitted on the SSE `done` event when no
 * in-flight streaming message exists to finalize.
 *
 * Returns null if `attachmentsSerialized` is empty or contains no
 * valid entries — caller must then fall through to the existing
 * `decideOnDoneFallback` path (which correctly suppresses the bare
 * `Done.` bubble for surface-owning tools).
 *
 * BUG-DR-INLINE-REPORT-FINALIZE (2026-05-21, follow-up to PR #219,
 * Slack C0AM8U804KZ/1778281880.499349 msg 1779387730.553859,
 * attachment F0B4YQ14E4F.pdf). PR #219 emitted the PDF + DOCX chips
 * but had no path to surface the report body, so chat showed
 * `Message received.` + chips only. The backend now ships the full
 * report markdown on the `done` event as `surfaceOwnedText` and the
 * frontend passes it in as `reportText` + `reportHtml` (caller is
 * responsible for the markdown→HTML conversion so this helper stays
 * pure / DOM-free / test-friendly).
 *
 * The returned message:
 *   - text=reportText || '' / html=reportHtml || '' — the deliverable
 *     IS the response surface; for surface-owning DR full-pipeline
 *     turns these carry the inline report markdown + rendered HTML,
 *     and for clarifying-card turns or attachment-only fallbacks they
 *     remain empty (regression guard for PR #219).
 *   - isUser=false, sourceTag='research_result' — renders the DR
 *     attachment variant with download buttons.
 *   - timestamp=now — caller can override by spreading.
 *
 * Pure: no React, no clock injection, no I/O, no markdown parsing.
 * The caller (useGlobalTools onDone) wires in markdownToHtml.
 *
 * @param attachmentsSerialized PDF/DOCX wire payload from SSE done
 * @param reportText Optional inline report markdown
 * @param reportHtml Optional inline report HTML (caller-rendered)
 */
export function buildResearchDeliverableMessage(
  attachmentsSerialized:
    | ReadonlyArray<RawDeliverableAttachment | null | undefined>
    | undefined
    | null,
  reportText?: string,
  reportHtml?: string,
  // Token usage (2026-07-13) — pre-mapped ChatMessage-shaped usage fields
  // (built by buildMessageTokenUsage) so the ⓘ popup shows real compute on
  // the full-pipeline DR deliverable instead of "no extensive compute".
  usage?: { tokenUsage?: TokenUsageMetadata; tokenUsages?: TokenUsageMetadata[] },
  // Convergence anchor (2026-09-18) — see the return below.
  serverTaskId?: string | null,
): ChatMessage | null {
  const normalizedText = typeof reportText === 'string' ? reportText : '';
  const normalizedHtml = typeof reportHtml === 'string' ? reportHtml : '';
  const hasBody = normalizedText.trim().length > 0 || normalizedHtml.trim().length > 0;

  const builtAttachments: SerializedAttachment[] = (attachmentsSerialized || [])
    .filter(
      (att): att is RawDeliverableAttachment =>
        Boolean(att) &&
        typeof att === 'object' &&
        typeof (att as RawDeliverableAttachment).name === 'string' &&
        (att as RawDeliverableAttachment).name.length > 0,
    )
    // 2026-09-08 (smooth playback): the ONE attachment mapper — durable locators
    // (storagePath / gcsPath included) survive; a base64 body only when nothing durable exists.
    .map((att) => toSerializedAttachment(att as RawAttachmentRecord))
    .filter((att): att is SerializedAttachment => att !== null);

  if (!hasBody && builtAttachments.length === 0) {
    return null;
  }

  // THE ANCHOR (owner report, 2026-09-18 — a Deep Research report arrived in the
  // thread TWICE, once whole and once cut off with the storage-truncation
  // marker). This bubble used to carry no id, no serverTaskId and no
  // taskAnchorId, while the server writes its own durable copy of the same
  // answer keyed `prest0n_task_<taskId>` (taskQueue's recoveredMessageId). One
  // anchorless copy and one anchored copy cannot be the same message to ANY of
  // the convergence layers — mergeLegacyAndPerDoc / perDocMessageId /
  // mergeRemoteThreadMessagesIntoLocal / mergeRecoveredAnswerIntoMessages — and
  // the byte-equal text fallback in dedupeAdjacentIdenticalAnswers could not
  // save it either, because a report is far over the persisted body cap so the
  // two copies genuinely differ. Exactly the failure mode
  // duplicateAnswerConvergence.test.ts opens by describing; the sibling
  // streaming-finalize branch in useGlobalTools has always stamped it.
  //
  // Absent id ⇒ the object is byte-identical to before, so a turn with no
  // server task (guest lanes, legacy callers) is unchanged.
  const taskAnchorId = deriveTaskAnchorId(serverTaskId);

  return {
    text: normalizedText,
    html: normalizedHtml,
    isUser: false,
    timestamp: new Date(),
    sourceTag: 'research_result',
    attachmentsSerialized: builtAttachments,
    ...(serverTaskId ? { serverTaskId } : {}),
    ...(taskAnchorId ? { taskAnchorId } : {}),
    ...(usage?.tokenUsage ? { tokenUsage: usage.tokenUsage } : {}),
    ...(usage?.tokenUsages && usage.tokenUsages.length > 0
      ? { tokenUsages: usage.tokenUsages }
      : {}),
  };
}
